import type { CreateLocationRequest, LocationFormValues } from '@/definitions/location'
import { toDigits } from '@/utils/phoneUtils'
import { toPostalDigits } from '@/utils/postalUtils'

/**
 * Maps the collected form values to the location create request.
 *
 * `state` and `suite` are the only fields the endpoint accepts as absent, so they
 * are the only ones typed optional here. Blank is omitted rather than sent: the
 * columns read "not filled in" as null, and an empty `state` is not a valid enum
 * member so it would be rejected outright. Every form collecting these fields
 * also gates on `state`, so in practice only `suite` actually arrives absent.
 *
 * The phone and ZIP are sent as bare digits, which is both how they are stored and
 * all the endpoint accepts — it validates the value rather than correcting it. The
 * form lets an operator type the punctuation they are used to, so stripping it is
 * this mapping's job.
 */
export const buildLocationRequest = (
  values: LocationFormValues
): CreateLocationRequest => {
  const suite = values.suite.trim()

  return {
    display_name: values.location_name.trim(),
    street: values.street.trim(),
    city: values.city.trim(),
    postal_code: toPostalDigits(values.postal_code),
    primary_phone: toDigits(values.primary_phone),
    timezone: values.timezone,
    ...(values.state && { state: values.state }),
    ...(suite && { suite }),
  }
}
